import { useState, useEffect } from 'react'
import Modal from '../Modal/Modal'
import API from '../../services/apiService'
import { Skeleton, SkeletonAvatar } from '../Skeleton/Skeleton'
import { useTranslation } from '../../i18n/LanguageContext'
import './CharacterModal.css'

// Authenticated Image Component - ROBUSTO COM LOGS DETALHADOS
const AuthImage = ({ clientId, filename, alt, className, onLoad, onError }) => {
  const [imageSrc, setImageSrc] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  useEffect(() => {
    let mounted = true
    let objectUrl = null

    const loadImage = async () => {
      if (!clientId || !filename) {
        console.error('[AuthImage] ❌ Faltam parâmetros:', { clientId, filename })
        setError(true)
        setLoading(false)
        if (onError) onError()
        return
      }

      setLoading(true)
      setError(false)

      try {
        const result = await API.Images.get(clientId, filename)
        
        if (!mounted) {
          return
        }

        if (result.success && result.blob) {
          // Revoke old URL before creating new one
          if (objectUrl) {
            URL.revokeObjectURL(objectUrl)
          }
          
          objectUrl = URL.createObjectURL(result.blob)
          
          setImageSrc(objectUrl)
          setError(false)
          if (onLoad) onLoad()
        } else {
          console.error('[AuthImage] ❌ API retornou erro:', result.error)
          setError(true)
          if (onError) onError()
        }
      } catch (err) {
        if (mounted) {
          console.error('[AuthImage] ❌ Exceção ao carregar:', {
            filename,
            error: err.message,
            stack: err.stack
          })
          setError(true)
          if (onError) onError()
        }
      } finally {
        if (mounted) {
          setLoading(false)
        }
      }
    }

    loadImage()

    return () => {
      mounted = false
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl)
      }
    }
  }, [clientId, filename])

  if (loading) {
    return (
      <div className="char__avatar-loading">
        <Skeleton width="100%" height="100%" borderRadius="50%" />
      </div>
    )
  }
  
  if (error || !imageSrc) {
    return null
  }

  return <img src={imageSrc} alt={alt} className={className} />
}

// Icons
const LevelIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" className="char__icon">
    <path d="M12 2l3 7h7l-5.5 4.5 2 7.5-6.5-4.5-6.5 4.5 2-7.5L2 9h7l3-7z" fill="currentColor"/>
  </svg>
)

const WorldIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" className="char__icon">
    <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="1.5"/>
    <ellipse cx="12" cy="12" rx="4" ry="10" fill="none" stroke="currentColor" strokeWidth="1"/>
    <line x1="2" y1="12" x2="22" y2="12" stroke="currentColor" strokeWidth="1"/>
  </svg>
)

const CalendarIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" className="char__icon">
    <rect x="3" y="4" width="18" height="18" rx="2" fill="none" stroke="currentColor" strokeWidth="2"/>
    <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="2"/>
  </svg>
)

const TrashIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" width="18" height="18" className={className} aria-hidden="true">
    <path d="M9 3h6l1 2h4v2H4V5h4l1-2z" fill="currentColor"/>
    <path d="M7 9h2v9H7zM11 9h2v9h-2zM15 9h2v9h-2z" fill="currentColor"/>
    <path d="M6 7h12l-1 13H7L6 7z" fill="none" stroke="currentColor" strokeWidth="1.5"/>
  </svg>
)

const CharacterModal = ({ isOpen, onClose, characterData, onDelete, user }) => {
  const [activeTab, setActiveTab] = useState('character')
  const [showFallbackIcon, setShowFallbackIcon] = useState(true)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const { t } = useTranslation()

  if (!isOpen || !characterData) return null

  const gameData = characterData.gameData || {}
  const clientId = characterData.clientId
  
  // Nome da imagem
  const imageName = characterData.name && characterData.world 
    ? `${characterData.name.toLowerCase().replace(/\s+/g, '_')}_${characterData.world.toLowerCase().replace(/\s+/g, '_')}`
    : null


  const formatNumber = (num) => {
    if (num === undefined || num === null) return '—'
    return Number(num).toLocaleString('en-US')
  }

  const formatDate = (dateString) => {
    if (!dateString) return '—'
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true)
  }

  const handleDeleteConfirm = async () => {
    setIsDeleting(true)
    try {
      await onDelete(characterData.name, characterData.world)
      onClose()
    } catch (error) {
      console.error('Failed to delete character:', error)
      alert(t('character.deleteFailed'))
    } finally {
      setIsDeleting(false)
      setShowDeleteConfirm(false)
    }
  }

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false)
  }

  // Stats da API (stats.hp e stats.mp são porcentagens 0-100)
  const hp = gameData.stats?.hp ?? '—'
  const mp = gameData.stats?.mp ?? '—'

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="">
      <div className="char">
        {/* Header */}
        <div className="char__header">
          <button 
            className="char__delete-btn" 
            onClick={handleDeleteClick}
            disabled={isDeleting}
            title={t('character.deleteTitle')}
            aria-label={t('character.deleteTitle')}
            aria-busy={isDeleting}
          >
            {isDeleting ? (
              <span className="char__delete-spinner" aria-hidden="true" />
            ) : (
              <TrashIcon className="char__delete-icon" />
            )}
          </button>
          <div className="char__avatar-section">
            <div className="char__avatar">
              {clientId && imageName ? (
                <>
                  <AuthImage
                    clientId={clientId}
                    filename={imageName}
                    alt={characterData.name}
                    className="char__avatar-img"
                    onLoad={() => setShowFallbackIcon(false)}
                    onError={() => setShowFallbackIcon(true)}
                  />
                  {showFallbackIcon && (
                    <span className="char__avatar-fallback">⚔️</span>
                  )}
                </>
              ) : (
                <span className="char__avatar-fallback">⚔️</span>
              )}
            </div>
            <div className="char__level-badge">
              <LevelIcon />
              <span>{t('character.level', { n: gameData.level || '?' })}</span>
            </div>
          </div>

          <div className="char__header-info">
            <h2 className="char__name">{characterData.name}</h2>
            <div className="char__subtitle">
              <WorldIcon />
              <span>{t('character.world', { world: characterData.world || t('character.unknown') })}</span>
            </div>
            <div className="char__status-text">
              {gameData.online ? `🟢 ${t('character.online')}` : `⚫ ${t('character.offline')}`}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="char__tabs">
          <button 
            className={`char__tab ${activeTab === 'character' ? 'char__tab--active' : ''}`}
            onClick={() => setActiveTab('character')}
          >
            {t('character.tabCharacter')}
          </button>
          <button 
            className={`char__tab ${activeTab === 'combat' ? 'char__tab--active' : ''}`}
            onClick={() => setActiveTab('combat')}
          >
            {t('character.combat')}
          </button>
          <button 
            className={`char__tab ${activeTab === 'automation' ? 'char__tab--active' : ''}`}
            onClick={() => setActiveTab('automation')}
          >
            {t('character.automation')}
          </button>
          <button 
            className={`char__tab ${activeTab === 'technical' ? 'char__tab--active' : ''}`}
            onClick={() => setActiveTab('technical')}
          >
            {t('character.technical')}
          </button>
        </div>

        {/* Content */}
        <div className="char__content">
          {/* Character Tab */}
          {activeTab === 'character' && (
            <div className="char__overview">
              <div className="char__stats-grid">
                <div className="char__stat-card">
                  <span className="char__stat-icon">💚</span>
                  <div className="char__stat-info">
                    <span className="char__stat-label">{t('character.hp')}</span>
                    <span className="char__stat-value">{hp}%</span>
                  </div>
                </div>

                <div className="char__stat-card">
                  <span className="char__stat-icon">💙</span>
                  <div className="char__stat-info">
                    <span className="char__stat-label">{t('character.mp')}</span>
                    <span className="char__stat-value">{mp}%</span>
                  </div>
                </div>

                <div className="char__stat-card">
                  <span className="char__stat-icon">🎯</span>
                  <div className="char__stat-info">
                    <span className="char__stat-label">{t('character.experience')}</span>
                    <span className="char__stat-value">{formatNumber(gameData.experience)}</span>
                  </div>
                </div>

                <div className="char__stat-card">
                  <span className="char__stat-icon">📈</span>
                  <div className="char__stat-info">
                    <span className="char__stat-label">{t('character.toNextLevel')}</span>
                    <span className="char__stat-value">{formatNumber(gameData.experienceToNextLevel)}</span>
                  </div>
                </div>

                <div className="char__stat-card">
                  <span className="char__stat-icon">💰</span>
                  <div className="char__stat-info">
                    <span className="char__stat-label">{t('character.gold')}</span>
                    <span className="char__stat-value">{formatNumber(gameData.gold)}</span>
                  </div>
                </div>

                <div className="char__stat-card">
                  <span className="char__stat-icon">⏱️</span>
                  <div className="char__stat-info">
                    <span className="char__stat-label">{t('character.stamina')}</span>
                    <span className="char__stat-value">{gameData.stamina || '—'}</span>
                  </div>
                </div>

                <div className="char__stat-card">
                  <span className="char__stat-icon">🛡️</span>
                  <div className="char__stat-info">
                    <span className="char__stat-label">{t('character.lifeInsurance')}</span>
                    <span className="char__stat-value">{gameData.lifeInsurance || '—'}</span>
                  </div>
                </div>

                <div className="char__stat-card">
                  <span className="char__stat-icon">🏆</span>
                  <div className="char__stat-info">
                    <span className="char__stat-label">{t('character.highscore')}</span>
                    <span className="char__stat-value">{gameData.rankings?.highscore || '—'}</span>
                  </div>
                </div>
              </div>

              <div className="char__info-list" style={{ marginTop: 'var(--space-md)' }}>
                {gameData.guild?.name && gameData.guild.name !== 'None' && (
                  <>
                    <div className="char__section-title">🏰 {t('character.guild')}</div>
                    <div className="char__info-item">
                      <span className="char__info-label">{t('character.name')}</span>
                      <span className="char__info-value">{gameData.guild.name}</span>
                    </div>
                    <div className="char__info-item">
                      <span className="char__info-label">{t('character.rank')}</span>
                      <span className="char__info-value">{gameData.guild.rank}</span>
                    </div>
                  </>
                )}

                {gameData.rankings && (
                  <>
                    <div className="char__section-title" style={{ marginTop: gameData.guild?.name && gameData.guild.name !== 'None' ? 'var(--space-md)' : '0' }}>🏆 {t('character.rankings')}</div>
                    <div className="char__info-item">
                      <span className="char__info-label">⚔️ {t('character.pvpRank')}</span>
                      <span className="char__info-value">{gameData.rankings.pvp}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Combat Tab */}
          {activeTab === 'combat' && (
            <div className="char__stats">
              <div className="char__info-list">
                <div className="char__section-title">🚀 {t('character.boosts')}</div>
                <div className="char__info-item">
                  <span className="char__info-label">{t('character.epBoost')}</span>
                  <span className="char__info-value">{gameData.boosts?.epBoost || 'No'}</span>
                </div>
                <div className="char__info-item">
                  <span className="char__info-label">{t('character.goldBoost')}</span>
                  <span className="char__info-value">{gameData.boosts?.goldBoost || 'No'}</span>
                </div>
                <div className="char__info-item">
                  <span className="char__info-label">{t('character.itemBonusEP')}</span>
                  <span className="char__info-value">{gameData.boosts?.itemBonusEP || '0%'}</span>
                </div>
                <div className="char__info-item">
                  <span className="char__info-label">{t('character.itemBonusGold')}</span>
                  <span className="char__info-value">{gameData.boosts?.itemBonusGold || '0%'}</span>
                </div>

                <div className="char__section-title" style={{ marginTop: 'var(--space-md)' }}>🛡️ {t('character.protections')}</div>
                <div className="char__info-item">
                  <span className="char__info-label">{t('character.hit')}</span>
                  <span className="char__info-value">{gameData.protections?.hit || '0%'}</span>
                </div>
                <div className="char__info-item">
                  <span className="char__info-label">🔥 {t('character.fire')}</span>
                  <span className="char__info-value">{gameData.protections?.fire || '0%'}</span>
                </div>
                <div className="char__info-item">
                  <span className="char__info-label">❄️ {t('character.ice')}</span>
                  <span className="char__info-value">{gameData.protections?.ice || '0%'}</span>
                </div>
                <div className="char__info-item">
                  <span className="char__info-label">👻 {t('character.soul')}</span>
                  <span className="char__info-value">{gameData.protections?.soul || '0%'}</span>
                </div>
                <div className="char__info-item">
                  <span className="char__info-label">⚡ {t('character.lightning')}</span>
                  <span className="char__info-value">{gameData.protections?.lightning || '0%'}</span>
                </div>

                <div className="char__section-title" style={{ marginTop: 'var(--space-md)' }}>⚔️ {t('character.skills')}</div>
                <div className="char__info-item">
                  <span className="char__info-label">{t('character.attackSkill')}</span>
                  <span className="char__info-value">{gameData.botStats?.skillAtk || 'NULL'}</span>
                </div>
                <div className="char__info-item">
                  <span className="char__info-label">{t('character.defenseSkill')}</span>
                  <span className="char__info-value">{gameData.botStats?.skillDef || 'NULL'}</span>
                </div>
                <div className="char__info-item">
                  <span className="char__info-label">{t('character.currentMonster')}</span>
                  <span className="char__info-value">{gameData.botStats?.currentMonster || t('character.none')}</span>
                </div>
                <div className="char__info-item">
                  <span className="char__info-label">{t('character.killedMonsters')}</span>
                  <span className="char__info-value">{formatNumber(gameData.botStats?.killedMonsters)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Automation Tab */}
          {activeTab === 'automation' && (
            <div className="char__stats">
              <div className="char__info-list">
                <div className="char__section-title">📜 {t('character.script')}</div>
                <div className="char__info-item">
                  <span className="char__info-label">{t('character.scriptName')}</span>
                  <span className="char__info-value">{gameData.botStats?.scriptName || t('character.none')}</span>
                </div>
                <div className="char__info-item">
                  <span className="char__info-label">{t('character.currentAction')}</span>
                  <span className="char__info-value">{gameData.botStats?.currentAction || '—'}</span>
                </div>
                <div className="char__info-item">
                  <span className="char__info-label">{t('character.saCombo')}</span>
                  <span className="char__info-value">{gameData.botStats?.currentSACombo || '—'}</span>
                </div>
                <div className="char__info-item">
                  <span className="char__info-label">{t('character.saLine')}</span>
                  <span className="char__info-value">{gameData.botStats?.currentSALine || '0'}</span>
                </div>
                <div className="char__info-item">
                  <span className="char__info-label">{t('character.movementSpeed')}</span>
                  <span className="char__info-value">{gameData.botStats?.speed || '0'}</span>
                </div>
                <div className="char__info-item">
                  <span className="char__info-label">{t('character.autoMove')}</span>
                  <span className="char__info-value">{gameData.botStats?.startMove ? t('character.enabled') : t('character.disabled')}</span>
                </div>
                <div className="char__info-item">
                  <span className="char__info-label">{t('character.autoSA')}</span>
                  <span className="char__info-value">{gameData.botStats?.startSA ? t('character.enabled') : t('character.disabled')}</span>
                </div>

                <div className="char__section-title" style={{ marginTop: 'var(--space-md)' }}>📊 {t('character.performance')}</div>
                <div className="char__info-item">
                  <span className="char__info-label">💎 {t('character.expPerHour')}</span>
                  <span className="char__info-value">{formatNumber(gameData.botStats?.expPerHour)}</span>
                </div>
                <div className="char__info-item">
                  <span className="char__info-label">💰 {t('character.goldPerHour')}</span>
                  <span className="char__info-value">{formatNumber(gameData.botStats?.goldPerHour)}</span>
                </div>
                <div className="char__info-item">
                  <span className="char__info-label">{t('character.expReceived')}</span>
                  <span className="char__info-value">{gameData.botStats?.expReceived || '—'}</span>
                </div>
                <div className="char__info-item">
                  <span className="char__info-label">{t('character.goldReceived')}</span>
                  <span className="char__info-value">{gameData.botStats?.goldReceived || '—'}</span>
                </div>

                <div className="char__section-title" style={{ marginTop: 'var(--space-md)' }}>⏱️ {t('character.session')}</div>
                <div className="char__info-item">
                  <span className="char__info-label">{t('character.huntTime')}</span>
                  <span className="char__info-value">{gameData.botStats?.huntTime || '—'}</span>
                </div>
                <div className="char__info-item">
                  <span className="char__info-label">{t('character.totalSteps')}</span>
                  <span className="char__info-value">{formatNumber(gameData.botStats?.totalSteps)}</span>
                </div>
                <div className="char__info-item">
                  <span className="char__info-label">{t('character.reloginTime')}</span>
                  <span className="char__info-value">{gameData.botStats?.reloginTime || 'n/a'}</span>
                </div>
                <div className="char__info-item">
                  <span className="char__info-label">{t('character.lastDisconnect')}</span>
                  <span className="char__info-value">{gameData.botStats?.lastDisconnect || '—'}</span>
                </div>
              </div>
            </div>
          )}

          {/* Technical Tab */}
          {activeTab === 'technical' && (
            <div className="char__details">
              <div className="char__info-list">
                <div className="char__section-title">🔧 {t('character.connection')}</div>
                <div className="char__info-item">
                  <span className="char__info-label">{t('character.state')}</span>
                  <span className="char__info-value">{gameData.botStats?.disconnectState || '—'}</span>
                </div>
                <div className="char__info-item">
                  <span className="char__info-label">{t('character.serverInfo')}</span>
                  <span className="char__info-value">{gameData.botStats?.serverInfo || '—'}</span>
                </div>
                <div className="char__info-item">
                  <span className="char__info-label">{t('character.premium')}</span>
                  <span className="char__info-value">{gameData.botStats?.Premium || '—'}</span>
                </div>
                <div className="char__info-item">
                  <span className="char__info-label">{t('character.banStatus')}</span>
                  <span className="char__info-value">{gameData.botStats?.banStatus || '—'}</span>
                </div>
                <div className="char__info-item">
                  <span className="char__info-label">{t('character.bind')}</span>
                  <span className="char__info-value">{gameData.botStats?.bind || '—'}</span>
                </div>

                <div className="char__section-title" style={{ marginTop: 'var(--space-md)' }}>🗺️ {t('character.location')}</div>
                {gameData.position && (
                  <>
                    <div className="char__info-item">
                      <span className="char__info-label">{t('character.x')}</span>
                      <span className="char__info-value">{gameData.position.x}</span>
                    </div>
                    <div className="char__info-item">
                      <span className="char__info-label">{t('character.y')}</span>
                      <span className="char__info-value">{gameData.position.y}</span>
                    </div>
                    <div className="char__info-item">
                      <span className="char__info-label">{t('character.z')}</span>
                      <span className="char__info-value">{gameData.position.z}</span>
                    </div>
                  </>
                )}

                <div className="char__section-title" style={{ marginTop: 'var(--space-md)' }}>🔑 {t('character.identifiers')}</div>
                <div className="char__info-item">
                  <span className="char__info-label">{t('character.characterId')}</span>
                  <span className="char__info-value">{characterData.id}</span>
                </div>
                <div className="char__info-item">
                  <span className="char__info-label">{t('character.clientId')}</span>
                  <span className="char__info-value">{characterData.clientId}</span>
                </div>

                <div className="char__section-title" style={{ marginTop: 'var(--space-md)' }}>📅 {t('character.timestamps')}</div>
                <div className="char__info-item">
                  <CalendarIcon />
                  <span className="char__info-label">{t('character.created')}</span>
                  <span className="char__info-value">{formatDate(characterData.createdAt)}</span>
                </div>
                <div className="char__info-item">
                  <CalendarIcon />
                  <span className="char__info-label">{t('character.lastUpdated')}</span>
                  <span className="char__info-value">{formatDate(characterData.updatedAt)}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="char__delete-confirm-overlay" onClick={handleDeleteCancel}>
            <div className="char__delete-confirm" onClick={(e) => e.stopPropagation()}>
              <h3 className="char__delete-title">⚠️ {t('character.confirmDelete')}</h3>
              <p className="char__delete-message">
                {t('character.deleteMessage', { name: characterData.name, world: characterData.world })}
              </p>
              <p className="char__delete-warning">
                {t('character.deleteWarning')}
              </p>
              <div className="char__delete-actions">
                <button 
                  className="char__delete-cancel-btn" 
                  onClick={handleDeleteCancel}
                  disabled={isDeleting}
                >
                  {t('account.cancel')}
                </button>
                <button 
                  className="char__delete-confirm-btn" 
                  onClick={handleDeleteConfirm}
                  disabled={isDeleting}
                >
                  {isDeleting ? t('character.deleting') : t('character.delete')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}

export default CharacterModal
